import { Hub } from 'aws-amplify';
import { NOT_PROVISIONED_SIGNATURE } from 'app/auth/ssoAttemptLog';

/**
 * SEE-1170/SEE-1200: capture SSO sign-in failures on web.
 *
 * When a federated (SSO) sign-in is rejected (PreSignup gate, missing IdP
 * attributes, SAML config errors), Cognito returns the error in the OAuth
 * callback — but aws-amplify v4 CONSUMES that callback
 * (Auth._handleAuthResponse) and dispatches it on the Hub as
 * `cognitoHostedUI_failure`; it never lands in the page URL. So reading
 * `error_description` from the query string (which works for the mobile
 * shell's forwarded params and hand-built URLs) misses the real thing on web.
 * The Hub is the only reliable source.
 *
 * The listener latches the full failure MESSAGE (not just one signature) so
 * consumers can classify it with classifySsoError and give registration-
 * required / missing-attribute / SAML failures the same handling as the URL
 * path. Unrecognized messages classify as 'other' and consumers stay silent
 * on them — the user-discovery guard is enforced at the consumer, not here.
 *
 * Timing: Amplify processes the callback at app boot, so this listener is
 * registered at MODULE LOAD (import it eagerly from index.tsx) — before React
 * renders — and it latches the result. Login reads the latched value on mount
 * (covers "fired before mount") and also subscribes (covers "fires after").
 */

/**
 * Substring of the PreSignup gate's rejection ("User does not have access to
 * the application"). Defined in ssoAttemptLog (Hub-free) and re-exported here
 * for existing importers.
 */
export { NOT_PROVISIONED_SIGNATURE };

let latchedMessage: string | null = null;
const listeners = new Set<(message: string) => void>();

function messageOf(data: unknown): string {
  if (!data) return '';
  let raw: string;
  if (typeof data === 'string') {
    raw = data;
  } else {
    const d = data as { message?: string; error_description?: string };
    raw = d.message || d.error_description || JSON.stringify(data);
  }
  // Cognito's OAuth error text arrives URL-encoded (spaces as '+', ':' as
  // '%3A'); normalize so the signature match works against plain text.
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' '));
  } catch {
    return raw.replace(/\+/g, ' ');
  }
}

Hub.listen('auth', ({ payload }) => {
  if (payload.event !== 'cognitoHostedUI_failure') return;
  const message = messageOf(payload.data);
  if (!message) return;
  // Hand the failure to a live subscriber if one is mounted; otherwise latch it
  // for the next Login to consume. Latching only when there's no listener keeps
  // the message from surviving past the subscriber that already handled it,
  // which would otherwise re-fire on a later SPA-navigated Login mount.
  if (listeners.size > 0) {
    listeners.forEach((cb) => cb(message));
  } else {
    latchedMessage = message;
  }
});

/** Latched SSO failure message, if one fired before a subscriber mounted; reading clears it. */
export function consumeSsoFailure(): string | null {
  const value = latchedMessage;
  latchedMessage = null;
  return value;
}

/** Notified with the failure message when an SSO failure fires after subscription. */
export function subscribeSsoFailure(cb: (message: string) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
